<script setup>
import FormBase from "./FormBase.vue";
import { FormInput } from "@Components/Form";
import { useForm } from "@/form";
import { useI18n } from "vue-i18n";
import { OpenAIIcon } from "@Icons/index";
import { useStore } from "@/Stores/requiredSettings";
import { ref } from "vue";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import ActionMessage from "@Components/ActionMessage.vue";
import { PrimaryButton, SecondaryButton } from "@Components/Button";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";

const props = defineProps({
    settings: Object,
});

const { t: __ } = useI18n();
const store = useStore();
const signature = ref();
const validateSignatureModalOpen = ref(false);

const form = useForm({
    api_key: props.settings?.api_key,
});

const validateSignatureForm = useForm({
    api_key: props.settings?.api_key,
    force: false,
});

const forceValidateSignature = async () => {
    if (validateSignatureForm.processing) return;

    validateSignatureForm.api_key = form.api_key;
    validateSignatureForm.force = true;

    const response = await validateSignatureForm.post("settings_api_validate_openai");

    if (response.success) {
        validateSignatureModalOpen.value = false;

        await form.post("settings_api_update_openai");
        store.refresh();
    } else {
        // todo: error
    }
};

const submit = async () => {
    if (form.processing || validateSignatureForm.processing) return;

    if (form.api_key.length > 0) {
        validateSignatureForm.api_key = form.api_key;
        validateSignatureForm.force = false;

        const response = await validateSignatureForm.post("settings_api_validate_openai");

        if (response.success) {
            await form.post("settings_api_update_openai");
            store.refresh();
        } else {
            signature.value = response.openai_signature;
            validateSignatureModalOpen.value = true;
        }
    } else {
        await form.post("settings_api_update_openai");
        store.refresh();
    }
};
</script>

<template>
    <FormBase @submit="submit" :form="form" :icon="OpenAIIcon" required>
        <template #title>
            {{ __("OpenAI") }}
        </template>
        <template #description>
            <span class="block">{{ __("Utilisez les modèles OpenAI GPT pour la génération de texte et Dall·E pour la génération d'images.") }} </span>
            <span
                class="block mt-1"
                v-html="
                    __('Consultez vos {startlink}limites{endlink} d\'utilisation.', {
                        startlink:
                            '<a href=\'https://platform.openai.com/account/limits\' target=\'_blank\' class=\'font-bold text-gray-900 dark:text-white hover:underline\'>',
                        endlink: '</a>',
                    })
                "></span>
        </template>
        <template #form>
            <FormInput
                v-model="form.api_key"
                :label="__('Clé API')"
                :errors="
                    form.errors.has('api_key')
                        ? form.errors.get('api_key')
                        : validateSignatureForm.errors.has('api_key')
                        ? validateSignatureForm.errors.get('api_key')
                        : []
                "
                sensitive>
                <template #hint>
                    <a href="https://platform.openai.com/account/api-keys" target="_blank" class="font-bold text-gray-900 dark:text-white hover:underline">{{
                        __("Obtenir la clé API")
                    }}</a>
                </template>
            </FormInput>

            <TransitionRoot as="template" :show="validateSignatureModalOpen">
                <Dialog as="div" class="z-[100000] relative" @close="validateSignatureModalOpen = false">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0">
                        <div class="bg-opacity-75 fixed inset-0 bg-gray-900 transition-opacity" />
                    </TransitionChild>

                    <div class="z-[100000] overflow-y-auto fixed inset-0">
                        <div class="min-h-full flex justify-center items-end p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild
                                as="template"
                                enter="ease-out duration-300"
                                enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enter-to="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leave-from="opacity-100 translate-y-0 sm:scale-100"
                                leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel
                                    class="transform overflow-hidden relative px-4 pt-5 pb-4 text-left bg-white rounded-lg border border-gray-300 shadow-xl transition-all sm:w-full sm:max-w-lg sm:p-6 sm:my-8">
                                    <div class="sm:flex sm:items-start">
                                        <div
                                            class="flex-shrink-0 mx-auto w-12 h-12 flex justify-center items-center bg-yellow-500 rounded-full sm:w-10 sm:h-10 sm:mx-0">
                                            <ExclamationTriangleIcon class="w-6 h-6 text-white" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">{{
                                                __("Remplacement de la clé API OpenAI")
                                            }}</DialogTitle>
                                            <div class="mt-2 space-y-3">
                                                <div class="block text-sm text-gray-600">
                                                    {{ __("Vous êtes sur le point de remplacer votre clé API OpenAI.") }}
                                                </div>
                                                <div class="block text-sm text-gray-600">
                                                    {{ __("otomatic.ai n'autorise qu'une seule clé API OpenAI par compte.") }}
                                                </div>
                                                <div class="block text-sm text-gray-600">
                                                    {{ __("Le remplacement de la clé entrainera la désactivation des sites associés à l'ancienne clé.") }}
                                                </div>
                                                <div class="block text-sm text-gray-600">
                                                    {{ __("Clé actuellement utilisée :") }} **************{{ signature }}.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:flex sm:flex-row-reverse sm:mt-4">
                                        <PrimaryButton @click="forceValidateSignature" :processing="validateSignatureForm.processing" type="submit">
                                            {{ __("Je confirme remplacer ma clé") }}
                                        </PrimaryButton>
                                        <SecondaryButton @click="validateSignatureModalOpen = false">
                                            {{ __("Annuler") }}
                                        </SecondaryButton>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </template>
        <template #footer>
            <ActionMessage :on="form.recentlySuccessful" class="mr-3"> {{ __("Sauvegardé.") }} </ActionMessage>
            <PrimaryButton :processing="form.processing || validateSignatureForm.processing"> {{ __("Sauvegarder") }} </PrimaryButton>
        </template>
    </FormBase>
</template>
